import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Upload, ArrowLeft, Check, Sparkles, Camera } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioVisualizePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [studio, setStudio] = useState(null);
  const [wraps, setWraps] = useState([]);
  const [sampleCars, setSampleCars] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Visualizer state - simplified to 3 steps: upload, select wrap, generating
  const [step, setStep] = useState(1);
  const [carImage, setCarImage] = useState(null);
  const [carImageBase64, setCarImageBase64] = useState('');
  const [selectedWrap, setSelectedWrap] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studioRes, wrapsRes, carsRes] = await Promise.all([
          axios.get(`${API}/studios/public/${slug}`),
          axios.get(`${API}/wraps/public/${slug}`),
          axios.get(`${API}/sample-cars`)
        ]);
        setStudio(studioRes.data);
        setWraps(wrapsRes.data);
        setSampleCars(carsRes.data.cars);

        // Pre-select wrap from URL
        const wrapId = searchParams.get('wrap');
        if (wrapId) {
          const wrap = wrapsRes.data.find(w => w.id === wrapId);
          if (wrap) setSelectedWrap(wrap);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, searchParams]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCarImage(event.target.result);
      setCarImageBase64(event.target.result);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleSampleCarSelect = async (car) => {
    try {
      const response = await fetch(car.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = (event) => {
        setCarImage(event.target.result);
        setCarImageBase64(event.target.result);
        setStep(2);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      setCarImage(car.url);
      setCarImageBase64(car.url);
      setStep(2);
    }
  };

  const handleWrapSelect = async (wrap) => {
    setSelectedWrap(wrap);
    
    // Go straight to generating
    if (!carImageBase64) {
      toast.error('Please select a car image first');
      return;
    }

    setGenerating(true);
    setStep(3);

    try {
      const renderRes = await axios.post(`${API}/renders`, {
        studio_id: studio.id,
        wrap_id: wrap.id,
        car_image_base64: carImageBase64,
        finish: wrap.finish
      });

      const renderId = renderRes.data.id;
      await axios.post(`${API}/renders/${renderId}/generate`);
      navigate(`/s/${slug}/result/${renderId}`);
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('Failed to generate preview. Please try again.');
      setGenerating(false);
      setStep(2);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Studio Not Found</h1>
          <Link to="/" className="text-white hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const accentColor = studio.accent_color || '#FFFFFF';

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="visualize-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link 
              to={`/s/${slug}`}
              className="flex items-center gap-2 text-white/60 hover:text-white"
            >
              <ArrowLeft size={18} />
              <span className="font-bold" style={{ color: accentColor }}>{studio.name}</span>
            </Link>
            
            {/* Progress - simplified to 3 steps */}
            <div className="hidden md:flex items-center gap-2">
              {['Upload', 'Select Wrap', 'Generate'].map((s, i) => (
                <div key={i} className="flex items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      step > i + 1 ? 'bg-green-500 text-white' : 
                      step === i + 1 ? 'text-black' : 'bg-white/10 text-white/40'
                    }`}
                    style={step === i + 1 ? { backgroundColor: accentColor } : {}}
                  >
                    {step > i + 1 ? <Check size={14} /> : i + 1}
                  </div>
                  {i < 2 && <div className={`w-8 h-0.5 ${step > i + 1 ? 'bg-green-500' : 'bg-white/10'}`}></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Step 1: Upload */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Upload Your Car Photo</h1>
              <p className="text-white/60 mb-12">Or choose from our sample images</p>

              {/* Upload Zone */}
              <div 
                className="border-2 border-dashed border-white/20 rounded-sm p-12 mb-8 cursor-pointer hover:border-white/40 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <input 
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Upload className="w-12 h-12 mx-auto mb-4 text-white/40" />
                <p className="text-white/60 mb-2">Click to upload or drag and drop</p>
                <p className="text-white/40 text-sm">PNG, JPG up to 10MB</p>
              </div>

              {/* Photo Tips */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 mb-12 text-left max-w-lg mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <Camera size={18} style={{ color: accentColor }} />
                  <span className="font-semibold">Photo Tips</span>
                </div>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 text-green-500" />
                    3/4 angle works best
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 text-green-500" />
                    Good lighting, avoid heavy shadows
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 text-green-500" />
                    Keep the whole car visible
                  </li>
                </ul>
              </div>

              {/* Sample Cars */}
              <div>
                <p className="text-white/40 text-sm mb-4">Or try with a sample car:</p>
                <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
                  {sampleCars.map((car) => (
                    <button
                      key={car.id}
                      onClick={() => handleSampleCarSelect(car)}
                      className="aspect-video bg-[#0F0F0F] rounded-sm overflow-hidden hover:ring-2 transition-all"
                      style={{ '--tw-ring-color': accentColor }}
                      data-testid={`sample-car-${car.id}`}
                    >
                      <img src={car.thumbnail} alt={car.name} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Select Wrap - clicking a wrap starts generation immediately */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Select a Wrap</h1>
                  <p className="text-white/60">Click a wrap to visualize it on your car</p>
                </div>
                <button onClick={() => setStep(1)} className="text-white/40 hover:text-white text-sm">
                  ← Change photo
                </button>
              </div>

              {/* Car Preview */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-4 mb-8">
                <img 
                  src={carImage} 
                  alt="Your car" 
                  className="w-full max-h-64 object-contain rounded-sm"
                />
              </div>

              {/* Wrap Grid */}
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {wraps.map((wrap) => (
                  <button
                    key={wrap.id}
                    onClick={() => handleWrapSelect(wrap)}
                    disabled={generating}
                    className="group relative aspect-square rounded-sm overflow-hidden transition-all hover:ring-2 disabled:opacity-50"
                    style={{ '--tw-ring-color': accentColor }}
                    data-testid={`wrap-option-${wrap.id}`}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundColor: wrap.color_hex }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <p className="text-xs font-medium truncate">{wrap.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Generating */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-24"
            >
              <div className="spinner w-16 h-16 mx-auto mb-8" style={{ borderTopColor: accentColor }}></div>
              <h2 className="text-2xl font-bold mb-4">Generating Your Preview</h2>
              <p className="text-white/60">This may take up to 60 seconds...</p>
              {selectedWrap && (
                <p className="text-white/40 text-sm mt-4">Applying {selectedWrap.name} to your car</p>
              )}
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StudioVisualizePage;
